import React, { useState } from 'react';
import {
  Box,
  Button,
  VStack,
  Heading,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  HStack
} from '@chakra-ui/react';
import { DownloadSimple, Archive } from '@phosphor-icons/react';
// Removed incorrect import: import { مشابه } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../../firebase/config'; // Correct auth import
import JSZip from 'jszip';
// import { saveAs } from 'file-saver'; // Not using for now, will use manual <a> tag

export default function DataExporter() {
  const [isLoadingCoco, setIsLoadingCoco] = useState(false);
  const [isLoadingYolo, setIsLoadingYolo] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();
  const functions = getFunctions(); // Get Firebase Functions instance

  const getAuthPayload = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("User not authenticated.");
    }
    // const idToken = await currentUser.getIdToken(); // Not strictly needed if function checks custom claims via UID
    return { uid: currentUser.uid };
  };

  const handleDownloadCoco = async () => {
    setIsLoadingCoco(true);
    setError(null);
    try {
      const authPayload = await getAuthPayload();
      const callExportDataset = httpsCallable(functions, 'exportDataset');
      const result = await callExportDataset({ format: 'coco', auth: authPayload });

      if (result.data.success) {
        const cocoData = result.data.data;
        const jsonString = JSON.stringify(cocoData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'dataset_coco.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast({
          title: 'COCO dataset download started.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error(result.data.error || 'Failed to generate COCO dataset.');
      }
    } catch (err) {
      console.error('Error downloading COCO dataset:', err);
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to download COCO dataset: ${err.message}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingCoco(false);
    }
  };

  const handleDownloadYolo = async () => {
    setIsLoadingYolo(true);
    setError(null);
    try {
      const authPayload = await getAuthPayload();
      const callExportDataset = httpsCallable(functions, 'exportDataset');
      const result = await callExportDataset({ format: 'yolo', auth: authPayload });

      if (result.data.success) {
        const { yoloAnnotations, categories } = result.data.data;
        const zip = new JSZip();

        // Create dataset.yaml
        const yamlContent = `path: ../datasets/megalith_maps_yolo_export # dataset root dir
train: images/train # train images (relative to 'path')
val: images/val # val images (relative to 'path') - assuming all are for training for now
test: # test images (optional)

# Classes
names:
${categories.map((cat, index) => `  ${index}: ${cat}`).join('\n')}
`;
        zip.file('dataset.yaml', yamlContent);

        const imageFolder = zip.folder('images');
        const labelFolder = zip.folder('labels');

        const imageFetchPromises = [];

        for (const imageId in yoloAnnotations) {
          const item = yoloAnnotations[imageId];
          const imageName = `${item.filename || imageId}.jpg`; // Assuming jpg, might need to derive from URL or stored type
          const labelName = `${item.filename || imageId}.txt`;

          labelFolder.file(labelName, item.annotations);

          // Fetch image and add to zip
          imageFetchPromises.push(
            fetch(item.url) // Using direct fetch, assuming public R2 URLs or CORS allows
              .then(response => {
                if (!response.ok) throw new Error(`Failed to fetch image ${item.url}: ${response.statusText}`);
                return response.blob();
              })
              .then(blob => {
                imageFolder.file(imageName, blob);
              })
              .catch(err => {
                console.error(`Failed to process image ${item.url}:`, err);
                // Optionally add a placeholder or log error in zip
              })
          );
        }

        await Promise.all(imageFetchPromises);

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'dataset_yolo.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast({
          title: 'YOLO dataset download started.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error(result.data.error || 'Failed to generate YOLO dataset.');
      }
    } catch (err) {
      console.error('Error downloading YOLO dataset:', err);
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to download YOLO dataset: ${err.message}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingYolo(false);
    }
  };

  return (
    <Box p={5}>
      <VStack spacing={6} align="stretch">
        <Heading as="h2" size="lg">Dataset Exporter</Heading>
        <Text>
          Download all annotated image data in COCO or YOLO format for model training.
          This process may take some time depending on the dataset size.
        </Text>

        {error && (
          <Alert status="error">
            <AlertIcon />
            {error}
          </Alert>
        )}

        <HStack spacing={4}>
          <Button
            leftIcon={<DownloadSimple size={20} />}
            colorScheme="blue"
            onClick={handleDownloadCoco}
            isLoading={isLoadingCoco}
            loadingText="Generating COCO..."
            minW="200px"
          >
            Download COCO
          </Button>
          <Button
            leftIcon={<Archive size={20} />}
            colorScheme="green"
            onClick={handleDownloadYolo}
            isLoading={isLoadingYolo}
            loadingText="Generating YOLO..."
            minW="200px"
          >
            Download YOLO
          </Button>
        </HStack>

        {(isLoadingCoco || isLoadingYolo) && (
          <VStack spacing={2} align="center" mt={4}>
            <Spinner />
            <Text>Preparing dataset, please wait...</Text>
          </VStack>
        )}
      </VStack>
    </Box>
  );
}
